/**
 * RFC 9457 `application/problem+json` → typed errors (D10, docs/04 §4).
 *
 * The one rule that shapes this file: **clients branch on `code`.** Never on
 * `status`, never on `detail`, never on `title`. An unrecognised `code` is
 * handled by status class plus `retryable`, and its `detail` is shown to the
 * user rather than swallowed — a new server-side condition should degrade to a
 * useful message, not to "Something went wrong".
 *
 * `retryable` is the server's opinion and it is authoritative. This client does
 * not invent a per-code retry policy; §4.4.2 is explicit that a client which
 * retries a `retryable: false` request is a client that will place a duplicate
 * order.
 */

import type { components } from '@/api/generated/rest'

export type ErrorCode = components['schemas']['ErrorCode']
export type Problem = components['schemas']['Problem']

/** A `code` the generated enum does not carry — a server ahead of this build. */
export type UnknownErrorCode = string & { readonly __unknownErrorCode?: never }

export interface HttpErrorInit {
  status: number
  problem: Problem | null
  requestId: string | null
  retryAfterMs: number | null
  method: string
  path: string
  /** The raw body, when it was not parseable problem+json. */
  rawBody?: string | undefined
}

/**
 * Every non-2xx response becomes one of these — including a 404 from the
 * router and a 500 from the panic handler, both of which the spec guarantees
 * are problem+json.
 */
export class HttpError extends Error {
  override readonly name = 'HttpError'
  readonly status: number
  readonly problem: Problem | null
  readonly requestId: string | null
  readonly retryAfterMs: number | null
  readonly method: string
  readonly path: string
  readonly rawBody: string | undefined

  constructor(init: HttpErrorInit) {
    super(init.problem?.detail ?? init.problem?.title ?? `${init.method} ${init.path} failed with ${init.status}`)
    this.status = init.status
    this.problem = init.problem
    this.requestId = init.requestId
    this.retryAfterMs = init.retryAfterMs
    this.method = init.method
    this.path = init.path
    this.rawBody = init.rawBody
  }

  /** The only field to branch on. `null` when the server did not send a problem body. */
  get code(): ErrorCode | UnknownErrorCode | null {
    return (this.problem?.code as ErrorCode | undefined) ?? null
  }

  is(code: ErrorCode): boolean {
    return this.code === code
  }

  /** Structured, code-specific detail. Documented per code, additive-only. */
  get meta(): Record<string, unknown> {
    return (this.problem?.meta as Record<string, unknown> | undefined) ?? {}
  }

  /**
   * The server's opinion, with a fallback for the no-body case only. 5xx
   * without a body is a transport failure and retrying a GET is safe; anything
   * else is not retried on a guess.
   */
  get retryable(): boolean {
    if (this.problem && typeof this.problem.retryable === 'boolean') return this.problem.retryable
    return this.status >= 500
  }

  /** Both 401 codes mean the same thing to the transport: re-check the session. */
  get isAuthFailure(): boolean {
    return this.status === 401
  }
}

/** A request that never produced a response — offline, DNS, TLS, abort. */
export class NetworkError extends Error {
  override readonly name = 'NetworkError'
  readonly method: string
  readonly path: string
  /** `true` when the caller aborted, which is not a failure to report. */
  readonly aborted: boolean

  constructor(method: string, path: string, cause: unknown, aborted = false) {
    super(`${method} ${path} did not complete: ${describe(cause)}`, { cause })
    this.method = method
    this.path = path
    this.aborted = aborted
  }
}

export function isHttpError(value: unknown): value is HttpError {
  return value instanceof HttpError
}

export function hasCode(value: unknown, code: ErrorCode): boolean {
  return isHttpError(value) && value.is(code)
}

/**
 * Parse a problem body defensively. A malformed error body must not turn into
 * a second, confusing error — the status code is still true, and that is what
 * the caller ends up rendering.
 */
export function parseProblem(body: unknown): Problem | null {
  if (typeof body !== 'object' || body === null) return null
  const candidate = body as Partial<Problem>
  if (typeof candidate.code !== 'string' || typeof candidate.status !== 'number') return null
  return candidate as Problem
}

/** `Retry-After` is seconds or an HTTP-date; `meta.retry_after_ms` wins when present. */
export function retryAfterMsFrom(headerValue: string | null, problem: Problem | null): number | null {
  const fromMeta = (problem?.meta as { retry_after_ms?: unknown } | undefined)?.retry_after_ms
  if (typeof fromMeta === 'number' && Number.isFinite(fromMeta)) return fromMeta
  if (!headerValue) return null
  const seconds = Number(headerValue)
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000)
  return null
}

function describe(cause: unknown): string {
  if (cause instanceof Error) return cause.message
  return String(cause)
}
